import React, { useCallback } from 'react';

import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';

import Drawer from 'components/Drawer';
import IconFemtasy from 'components/IconFemtasy';
import Message from 'components/Message';
import Typography from 'components/Typography';

import { IntlMsgShape } from 'helpers/consts';

import { useQueue } from 'hooks/useQueue';
import { useQueueRemoval } from 'hooks/useQueueRemoval';
import { useTracking } from 'hooks/useTracking';

import {
  QueueMoveKind,
  QueueState,
  QueueStory,
  reorder,
} from 'store/modules/queue';
import ReduxState from 'store/ReduxState';

import './style.scss';

interface QueueListItemProps {
  sortId: string;
  item: QueueStory;
  onRemove: () => void;
  onClick: () => void;
}

const QueueListItem = ({
  sortId,
  item,
  onRemove,
  onClick,
}: QueueListItemProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform } =
    useSortable({ id: sortId, resizeObserverConfig: {} });

  const style = {
    transform: CSS.Transform.toString(transform),
  };

  return (
    <div ref={setNodeRef} style={style} className="QueueList__item">
      <div ref={setActivatorNodeRef} {...listeners} {...attributes}>
        <IconFemtasy icon="Move" className="QueueList__move" />
      </div>
      <Typography
        variant="h4"
        className="QueueList__itemContent"
        onClick={onClick}
      >
        {item.title} - {item.speaker.name}
      </Typography>
      <IconFemtasy
        icon="Trash-can-line"
        className="QueueList__remove"
        onClick={onRemove}
      />
    </div>
  );
};

interface SeparatorProps {
  title?: string | IntlMsgShape;
  onClearAll: () => void;
}

const Separator = ({ title, onClearAll }: SeparatorProps) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: 'separator',
    resizeObserverConfig: {},
    disabled: true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="QueueList__separator"
    >
      {title && (
        <Typography variant="h4">
          <Message message={title} />
        </Typography>
      )}

      <Typography
        variant="link-small-thick"
        className="QueueList__separator__clearAll"
        onClick={() => {
          onClearAll();
        }}
      >
        <Message message={{ id: 'SM0123.Queue.Clear all' }} />
      </Typography>
    </div>
  );
};

interface IProps {
  onClose: () => void;
}

export const sortableItemsForContext = (
  queue: Pick<QueueState, 'queue' | 'recommendations'>,
) => [
  ...queue.queue.map((_, index) => `queue-${index}`),
  ...(queue.recommendations.length > 0 ? ['separator'] : []),
  ...queue.recommendations.map((_, index) => `recommendation-${index}`),
];

export const onDragEndHandler = (
  event: DragEndEvent,
  queue: QueueState,
  dispatch,
  trackContentInteraction: (
    action: string,
    name: string,
    piece: string,
  ) => void,
) => {
  if (event.over) {
    const [fromKind, from] = (event.active.id as string).split('-');
    const [toKind, to] =
      event.over.id === 'separator' && fromKind === 'recommendation'
        ? ['queue', queue.queue.length]
        : (event.over.id as string).split('-');

    dispatch(
      reorder({
        fromKind: fromKind as QueueMoveKind,
        from: Number(from),
        toKind: toKind as QueueMoveKind,
        to: Number(to),
      }),
    );

    trackContentInteraction(
      'reorder',
      'reorder-items-in-queue',
      `reorder-items-in-queue-from-${event.active.id}-to-${event.over.id}`,
    );
  }
};

const QueueList = ({ onClose }: IProps) => {
  const dispatch = useDispatch();
  const { trackContentInteraction } = useTracking();
  const { playFromQueue, playFromRecommendations } = useQueue();
  const {
    onRemoveAllFromQueue,
    onRemoveAllFromRecommendations,
    onRemoveFromQueue,
    onRemoveFromRecommendations,
  } = useQueueRemoval();

  const queue = useSelector<ReduxState, QueueState>(({ queue }) => queue);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      onDragEndHandler(event, queue, dispatch, trackContentInteraction);
    },
    [dispatch, queue, trackContentInteraction],
  );

  const touchSensor = useSensor(TouchSensor);
  const mouseSensor = useSensor(MouseSensor);
  const sensors = useSensors(touchSensor, mouseSensor);

  return (
    <Drawer
      anchor="bottom"
      icon="Arrowdown"
      onClose={onClose}
      header={{ id: 'SM0123.Queue.title' }}
      open={true}
    >
      <div className="QueueList">
        <div className="QueueList__content">
          {queue.queue.length + queue.recommendations.length <= 0 && (
            <Typography variant="body">
              <Message message={{ id: 'SM0123.Queue.empty message' }} />
            </Typography>
          )}
          <DndContext
            sensors={sensors}
            modifiers={[
              restrictToVerticalAxis,
              restrictToWindowEdges,
              restrictToParentElement,
            ]}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableItemsForContext(queue)}>
              {queue.queue.length > 0 && (
                <Separator onClearAll={onRemoveAllFromQueue} />
              )}
              {queue.queue.map((item, index) => (
                <QueueListItem
                  sortId={`queue-${index}`}
                  item={item}
                  key={index}
                  onRemove={() => onRemoveFromQueue({ index, item })}
                  onClick={() => {
                    playFromQueue(index);
                    onClose();
                  }}
                />
              ))}
              {queue.recommendations.length > 0 && (
                <Separator
                  title={{ id: 'SM0123.Queue.Recomnedation title' }}
                  onClearAll={onRemoveAllFromRecommendations}
                />
              )}
              {queue.recommendations.map((item, index) => (
                <QueueListItem
                  sortId={`recommendation-${index}`}
                  item={item}
                  key={index}
                  onRemove={() => onRemoveFromRecommendations({ index, item })}
                  onClick={() => {
                    playFromRecommendations(index);
                    onClose();
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </Drawer>
  );
};

export default QueueList;
